// Diese Oberflaeche ist gegen ThingsBoard **Professional Edition** gebaut, die
// Testumgebung faehrt die **Community Edition**. Drei Sorten Endpunkte gibt es
// nur in PE, und CE antwortet darauf mit 404 — nachgeprueft an der
// /v3/api-docs einer laufenden CE 4.3.1.3:
//
//   /api/deviceInfos/all   ->  CE kennt /api/tenant/deviceInfos
//   /api/dashboards/all    ->  CE kennt /api/tenant/dashboards
//   /api/entityGroup*      ->  CE hat keine Entity Groups, kein Gegenstueck
//
// Die ersten beiden lassen sich ersetzen: Paging, Textsuche und Sortierung
// gibt es dort genauso, nur ohne includeCustomers, und "groups" fehlt in der
// Antwort (was hier nirgends stoert, die Gruppe "All" wurde ohnehin
// herausgefiltert). Fuer die Gruppen-Seiten gibt es keinen Ersatz; die sagen
// das selbst, indem sie einen 404 als "nur in der Professional Edition"
// ausweisen.
import { API_BASE, authHeaders } from '../constants/api';

const COMMUNITY_EQUIVALENT = {
  'deviceInfos/all': { path: 'tenant/deviceInfos' },
  // Ein Dashboard heisst in CE ueber "title", nicht ueber "name" - der
  // PE-Endpunkt nimmt beides, dieser antwortet auf "name" mit
  // 500 "Database error". Am laufenden CE 4.3.1.3 durchprobiert: bei den
  // Geraeten gehen name, createdTime und active unveraendert durch, bei den
  // Dashboards nur title und createdTime.
  'dashboards/all': { path: 'tenant/dashboards', sortProperty: { name: 'title' } },
};

// Kennt nur PE. CE ignoriert unbekannte Parameter zwar, aber mitzuschicken,
// was drueben nichts bedeutet, waere irrefuehrend.
const PROFESSIONAL_ONLY_PARAMS = ['includeCustomers'];

// null = noch nicht bekannt. Wird beim ersten Aufruf durch die Antwort selbst
// beantwortet und gilt dann fuer den Rest der Sitzung — ein eigener Rundruf
// zur Erkennung der Edition waere eine Anfrage mehr fuer dieselbe Auskunft.
let community = null;

/** Ob die Gegenstelle als Community Edition erkannt wurde. */
export function isCommunityEdition() {
  return community === true;
}

// Was die Gruppen-Seiten anzeigen, wenn ThingsBoard ihren Endpunkt nicht kennt.
// Ein Netzwerkfehler waere die falsche Auskunft: Es ist nichts kaputt, die
// Funktion gibt es in dieser Ausgabe schlicht nicht.
export const ENTITY_GROUPS_UNAVAILABLE =
  'Gruppen sind eine Funktion der ThingsBoard Professional Edition. ' +
  'Diese Installation läuft mit der Community Edition, die keine Entity Groups kennt.';

/**
 * Fehlertext fuer eine fehlgeschlagene Antwort eines Gruppen-Endpunkts.
 * Merkt sich dabei die Edition, damit spaetere Listen gleich den CE-Weg gehen.
 */
export function entityGroupError(response, fallback) {
  if (response.status === 404) {
    community = true;
    return ENTITY_GROUPS_UNAVAILABLE;
  }
  return fallback;
}

/**
 * Holt eine Liste bei ThingsBoard, notfalls ueber den CE-Endpunkt.
 *
 * @param {string} path    PE-Pfad, z.B. 'deviceInfos/all'
 * @param {object} params  Query-Parameter als einfaches Objekt
 * @param {string} token   ThingsBoard-JWT
 * @returns {Promise<Response>} die Antwort, die tatsaechlich etwas geliefert hat
 */
export async function fetchListing(path, params, token) {
  const ask = (endpoint, search) =>
    fetch(`${API_BASE}/${endpoint}?${search}`, { headers: authHeaders(token) });

  const full = new URLSearchParams(params);

  if (community !== true) {
    const response = await ask(path, full.toString());
    // Ein 404 auf einen Listenendpunkt heisst "gibt es hier nicht" — eine
    // leere Liste waere eine 200 mit data: [].
    if (response.status !== 404) {
      community = false;
      return response;
    }
    community = true;
  }

  const fallback = COMMUNITY_EQUIVALENT[path];
  if (!fallback) {
    return ask(path, full.toString());
  }

  const reduced = new URLSearchParams(full);
  PROFESSIONAL_ONLY_PARAMS.forEach((name) => reduced.delete(name));

  const sort = reduced.get('sortProperty');
  const renamed = fallback.sortProperty?.[sort];
  if (renamed) {
    reduced.set('sortProperty', renamed);
  }

  return ask(fallback.path, reduced.toString());
}
